import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Building2,
  Check,
  Clock,
  Globe,
  Lock,
  QrCode,
  SearchX,
  UserX,
  WifiOff,
} from 'lucide-react';
import type { PublicInvitation, RegisterResponse, RegistrationField } from '@up2d8/contracts';
import { problemCode } from '@up2d8/api-client';
import { Button, Card, Input, Select, cn } from '@up2d8/ui';
import { useSession, API_BASE } from '../../../shared/session';
import { PIN_LENGTH, PinPad } from '../pin-pad';
import { rememberTenantCode } from '../app-login-page';
import { rememberLoginMode } from '../../../shared/login-mode';
import { PushCard } from '../push-card';
import { isNativeApp } from '../../../shared/native';
import { useProductName } from '../../../shared/theme';
import {
  JOIN_LANGUAGES,
  JOIN_TEXTS,
  isJoinLanguage,
  joinTextsFor,
  type JoinLanguage,
} from './join-texts';

type ErrorKind = keyof (typeof JOIN_TEXTS)['nl']['errors'];
type Stage = 'code' | 'welcome' | 'details' | 'pin' | 'confirm' | 'done' | 'desktop';

const STORAGE_KEY = 'up2d8.language';

/** "K7QM24TX" → "K7QM-24TX". */
function formatCode(value: string): string {
  const compact = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
  return compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

function startLanguage(): JoinLanguage {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (isJoinLanguage(stored)) return stored;
  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'nl';
  return isJoinLanguage(browser) ? browser : 'nl';
}

/**
 * Zelfregistratie met een uitnodiging (Stap 34, REG-2, spec Zelfregistratie §2).
 *
 * Geen onderbalk - de gebruiker is nog niet aangemeld - en de hoofdknop in een vaste voet. Twee
 * ingangen: `/app/join` (de code intypen) en `/start/$code` (de link uit de QR-code), die meteen
 * naar het welkom gaat. Op een computer geopend toont `/start` eerst "open dit op je telefoon".
 *
 * De taal geldt meteen en gaat mee naar de server: daar wordt ze de voorkeurstaal, of de leestaal
 * als de interface ze niet kent (Pools, ADR-0043).
 */
export function AppJoinPage({ initialCode = null }: { initialCode?: string | null }) {
  const session = useSession();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [language, setLanguage] = useState<JoinLanguage>(startLanguage);

  const [code, setCode] = useState(initialCode ? formatCode(initialCode) : '');
  const [invitation, setInvitation] = useState<PublicInvitation | null>(null);
  // De woorden van het type organisatie zodra de uitnodiging bekend is (ORG-3): "je club", "je
  // lidnummer". Daarvoor die van een fabriek: de code kan van elke organisatie zijn.
  const tx = joinTextsFor(language, invitation?.orgType);
  const [stage, setStage] = useState<Stage>(() => {
    if (!initialCode) return 'code';
    const desktop =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(min-width: 900px) and (pointer: fine)').matches;
    return desktop ? 'desktop' : 'code';
  });
  const [error, setError] = useState<{ kind: ErrorKind; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [result, setResult] = useState<RegisterResponse | null>(null);

  // De interface volgt de keuze waar ze kan (nl, fr, en); Pools blijft in deze flow.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    if (language !== 'pl' && i18n.language !== language) void i18n.changeLanguage(language);
  }, [language, i18n]);

  const lookup = async (raw: string, lang: JoinLanguage) => {
    if (!navigator.onLine) {
      setError({ kind: 'OFFLINE', detail: '' });
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/public/invitations/${encodeURIComponent(raw)}?lang=${lang}`,
      );
      const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (!response.ok) {
        setError(errorFrom(body));
        return;
      }
      setInvitation(body as unknown as PublicInvitation);
      setError(null);
      setStage('welcome');
    } catch {
      setError({ kind: 'OFFLINE', detail: '' });
    } finally {
      setBusy(false);
    }
  };

  // De QR-link opent meteen het welkom (behalve op een computer, zie hierboven).
  const gestart = useRef(false);
  useEffect(() => {
    if (initialCode && stage === 'code' && !gestart.current) {
      gestart.current = true;
      void lookup(initialCode, language);
    }
  }, [initialCode, stage]);

  // Een andere taal kiezen haalt de velden opnieuw op, met hun label in die taal.
  const kiesTaal = (next: JoinLanguage) => {
    setLanguage(next);
    if (invitation) void lookup(code, next);
  };

  const controleerGegevens = (): boolean => {
    const fouten: Record<string, string> = {};
    if (!firstName.trim()) fouten.firstName = tx.fieldMissing(tx.firstName);
    if (!lastName.trim()) fouten.lastName = tx.fieldMissing(tx.lastName);
    for (const field of invitation?.fields ?? []) {
      if (field.required && !(fields[field.key] ?? '').trim()) {
        fouten[field.key] =
          field.type === 'employee_number'
            ? tx.employeeNumberMissing
            : tx.fieldMissing(field.label);
      }
    }
    setFieldErrors(fouten);
    return Object.keys(fouten).length === 0;
  };

  const registreer = async () => {
    if (!navigator.onLine) {
      setError({ kind: 'OFFLINE', detail: '' });
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/public/invitations/${encodeURIComponent(code)}/register`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ firstName, lastName, fields, pin, language }),
        },
      );
      const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (!response.ok) {
        if (body.code === 'WEAK_PIN') {
          setPin('');
          setConfirmation('');
          setPinError(tx.pinWeak);
          setStage('pin');
          return;
        }
        if (body.code === 'VALIDATION_FAILED') {
          // Terug naar de gegevens, mét de reden bij het veld: zonder melding lijkt "Ga verder"
          // gewoon niet te werken. `errors[].path` is `firstName`, `lastName` of `fields.<key>`.
          const errors = (body.errors as { path: string; message: string }[] | undefined) ?? [];
          const fouten: Record<string, string> = {};
          for (const item of errors) {
            const key = item.path.startsWith('fields.')
              ? item.path.slice('fields.'.length)
              : item.path;
            const field = invitation?.fields.find((candidate) => candidate.key === key);
            if (field) {
              fouten[key] =
                field.type === 'employee_number'
                  ? tx.employeeNumberMissing
                  : tx.fieldMissing(field.label);
            } else if (key === 'firstName' || key === 'lastName') {
              fouten[key] = item.message;
            }
          }
          // Een fout die bij geen enkel veld hoort, toch tonen: bij het eerste veld.
          if (Object.keys(fouten).length === 0) {
            fouten.firstName =
              errors[0]?.message ??
              (body.detail as string | undefined) ??
              tx.fieldMissing(tx.firstName);
          }
          setFieldErrors(fouten);
          setStage('details');
          return;
        }
        setError(errorFrom(body));
        return;
      }
      const registered = body as unknown as RegisterResponse;
      rememberTenantCode(registered.tenantCode);
      // Geregistreerd met een pincode: zo begint het aanmeldscherm de volgende keer (ORG-3).
      rememberLoginMode('pin');
      await session.adoptToken(registered.accessToken);
      setResult(registered);
      setStage('done');
    } catch {
      setError({ kind: 'OFFLINE', detail: '' });
    } finally {
      setBusy(false);
    }
  };

  // --- Frame 6: foutschermen -------------------------------------------------------------
  if (error) {
    const sjabloon = tx.errors[error.kind];
    const terug = () => {
      const kind = error.kind;
      setError(null);
      if (kind === 'EMPLOYEE_NUMBER_TAKEN') setStage('details');
      else if (kind === 'OFFLINE') return;
      else {
        setInvitation(null);
        setCode('');
        setStage('code');
      }
    };
    // Bij een onbekende code en te veel pogingen staat er geen bedrijfsnaam of logo.
    const anoniem = error.kind === 'UNKNOWN' || error.kind === 'RATE_LIMITED';
    return (
      <JoinShell
        company={anoniem ? null : (invitation?.tenantName ?? null)}
        language={language}
        onLanguage={kiesTaal}
        footer={
          sjabloon.button ? (
            <Button size="lg" className="w-full" onClick={terug} data-testid="join-error-action">
              {sjabloon.button}
            </Button>
          ) : null
        }
      >
        <StateScreen
          tone={error.kind === 'OFFLINE' ? 'neutral' : 'danger'}
          icon={ERROR_ICON[error.kind]}
          title={sjabloon.title}
          text={sjabloon.text(error.detail)}
          testId={`join-error-${error.kind}`}
        />
      </JoinShell>
    );
  }

  // --- Geopend op een computer (niet getekend; sjabloon van frame 6) -----------------------
  if (stage === 'desktop') {
    return (
      <JoinShell
        company={null}
        language={language}
        onLanguage={kiesTaal}
        footer={
          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            data-testid="join-desktop-continue"
            onClick={() => setStage('code')}
          >
            {tx.desktopContinue}
          </Button>
        }
      >
        <StateScreen
          tone="neutral"
          icon={<QrCode className="h-9 w-9" />}
          title={tx.desktopTitle}
          text={tx.desktopText}
          testId="join-desktop"
        >
          <img
            src={`${API_BASE}/api/v1/public/invitations/${encodeURIComponent(code)}/qr`}
            alt=""
            className="mx-auto h-48 w-48"
          />
        </StateScreen>
      </JoinShell>
    );
  }

  // --- Frame 1: code invoeren -------------------------------------------------------------
  if (stage === 'code') {
    return (
      <JoinShell
        company={null}
        language={language}
        onLanguage={kiesTaal}
        footer={
          <Button
            size="lg"
            className="w-full"
            loading={busy}
            disabled={code.replace('-', '').length !== 8}
            onClick={() => void lookup(code, language)}
            data-testid="join-code-next"
          >
            {tx.next}
          </Button>
        }
      >
        <h1 className="font-heading text-[22px] font-semibold text-text-primary">{tx.codeTitle}</h1>
        <p className="text-content text-text-secondary">{tx.codeIntro}</p>
        <CodeInput value={code} onChange={setCode} label={tx.codeLabel} hint={tx.codeHint} />
        <ScanButton label={tx.scan} hint={tx.scanHint} />
      </JoinShell>
    );
  }

  if (!invitation) return null;

  // --- Frame 2: welkom ---------------------------------------------------------------------
  if (stage === 'welcome') {
    return (
      <JoinShell
        company={invitation.tenantName}
        language={language}
        onLanguage={kiesTaal}
        footer={
          <Button
            size="lg"
            className="w-full"
            onClick={() => setStage('details')}
            data-testid="join-begin"
          >
            {tx.begin}
          </Button>
        }
      >
        <LanguageChips language={language} onChoose={kiesTaal} />
        <div className="flex items-center gap-3">
          <CompanyTile name={invitation.tenantName} logoUrl={invitation.tenantLogoUrl} />
          <h1 className="font-heading text-[22px] font-semibold text-text-primary">
            {tx.invites(invitation.tenantName)}
          </h1>
        </div>
        {invitation.destination ? (
          <Card compact className="flex items-start gap-3" data-testid="join-destination">
            <Building2
              aria-hidden
              className="mt-0.5 h-5 w-5 shrink-0 text-[var(--brand-primary-aa)]"
            />
            <div>
              <p className="font-heading text-content font-semibold text-text-primary">
                {tx.youJoin} · {invitation.destination}
              </p>
              <p className="text-small text-text-secondary">{tx.setByEmployer}</p>
            </div>
          </Card>
        ) : null}
        <section className="flex flex-col gap-3">
          <h2 className="font-heading text-[17px] font-semibold text-text-primary">
            {tx.howItWorks}
          </h2>
          <ol className="flex flex-col gap-3">
            {tx.steps.map((step, index) => (
              <li key={step} className="flex items-center gap-3 text-content text-text-primary">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-dark-blue font-heading text-small font-semibold text-text-on-dark">
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </section>
      </JoinShell>
    );
  }

  // --- Frame 3: je gegevens ----------------------------------------------------------------
  if (stage === 'details') {
    return (
      <JoinShell
        company={invitation.tenantName}
        language={language}
        onLanguage={kiesTaal}
        footer={
          <Button
            size="lg"
            className="w-full"
            onClick={() => {
              if (controleerGegevens()) setStage('pin');
            }}
            data-testid="join-details-next"
          >
            {tx.next}
          </Button>
        }
      >
        <Progress step={1} label={tx.stepOf(1)} />
        <h1 className="font-heading text-[22px] font-semibold text-text-primary">
          {tx.detailsTitle}
        </h1>
        <div className="flex flex-col gap-4">
          <Input
            label={`${tx.firstName} · ${tx.required}`}
            value={firstName}
            autoComplete="given-name"
            error={fieldErrors.firstName}
            onChange={(event) => setFirstName(event.target.value)}
            data-testid="join-first-name"
          />
          <Input
            label={`${tx.lastName} · ${tx.required}`}
            value={lastName}
            autoComplete="family-name"
            error={fieldErrors.lastName}
            onChange={(event) => setLastName(event.target.value)}
            data-testid="join-last-name"
          />
          {invitation.fields.map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={fields[field.key] ?? ''}
              error={fieldErrors[field.key]}
              suffix={field.required ? tx.required : tx.optional}
              onChange={(value) => setFields((current) => ({ ...current, [field.key]: value }))}
            />
          ))}
        </div>
      </JoinShell>
    );
  }

  // --- Frame 4 en 4b: PIN kiezen, twee keer ------------------------------------------------
  if (stage === 'pin' || stage === 'confirm') {
    const tweede = stage === 'confirm';
    return (
      <JoinShell
        company={invitation.tenantName}
        language={language}
        onLanguage={kiesTaal}
        footer={
          <Button
            size="lg"
            className="w-full"
            loading={busy}
            disabled={(tweede ? confirmation : pin).length !== PIN_LENGTH}
            data-testid="join-pin-next"
            onClick={() => {
              if (!tweede) {
                setPinError(null);
                setStage('confirm');
                return;
              }
              if (pin !== confirmation) {
                setPin('');
                setConfirmation('');
                setPinError(tx.pinMismatch);
                setStage('pin');
                return;
              }
              void registreer();
            }}
          >
            {tx.next}
          </Button>
        }
      >
        <Progress step={2} label={tx.stepOf(2)} />
        <h1 className="font-heading text-[22px] font-semibold text-text-primary">
          {tweede ? tx.pinRepeat : tx.pinTitle}
        </h1>
        <p className="text-content text-text-secondary">{tx.pinIntro}</p>
        {pinError && !tweede ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md bg-danger-tint p-3.5 text-small text-danger-tint-text"
            data-testid="join-pin-error"
          >
            <AlertCircle aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
            {pinError}
          </p>
        ) : null}
        <PinPad
          key={stage}
          value={tweede ? confirmation : pin}
          onChange={tweede ? setConfirmation : setPin}
          label={tweede ? tx.pinRepeat : tx.pinTitle}
        />
      </JoinShell>
    );
  }

  // --- Frame 5: klaar ----------------------------------------------------------------------
  return (
    <JoinShell
      company={invitation.tenantName}
      language={language}
      onLanguage={kiesTaal}
      footer={
        <Button
          size="lg"
          className="w-full"
          onClick={() => void navigate({ to: '/app' })}
          data-testid="join-to-app"
        >
          {tx.toApp}
        </Button>
      }
    >
      <StateScreen
        tone="success"
        icon={<Check className="h-9 w-9" strokeWidth={3} />}
        title={tx.doneTitle(result?.firstName ?? firstName)}
        text={tx.doneIntro}
        testId="join-done"
      >
        {result ? (
          <p className="rounded-md bg-border-light p-3.5 text-small text-text-secondary">
            {tx.doneLogin(result.tenantCode, result.employeeNumber)}
          </p>
        ) : null}
      </StateScreen>
      {/* De bestaande kaarten "Zet de app op je beginscherm" en "Zet meldingen aan". */}
      {isNativeApp() ? null : (
        <Card compact className="flex items-center justify-between gap-3">
          <span className="font-heading text-content font-semibold text-text-primary">
            {t('install.title')}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void navigate({ to: '/app/install' })}
          >
            {t('install.link')}
          </Button>
        </Card>
      )}
      <PushCard compact />
    </JoinShell>
  );
}

const ERROR_ICON: Record<ErrorKind, ReactNode> = {
  EXPIRED: <Clock className="h-9 w-9" />,
  REVOKED: <Lock className="h-9 w-9" />,
  FULL: <UserX className="h-9 w-9" />,
  UNKNOWN: <SearchX className="h-9 w-9" />,
  EMPLOYEE_NUMBER_TAKEN: <UserX className="h-9 w-9" />,
  AWAITING_APPROVAL: <Clock className="h-9 w-9" />,
  RATE_LIMITED: <Clock className="h-9 w-9" />,
  OFFLINE: <WifiOff className="h-9 w-9" />,
};

/** Van een probleemantwoord naar een foutscherm; nooit een foutcode op het scherm. */
function errorFrom(body: Record<string, unknown>): { kind: ErrorKind; detail: string } {
  const code = problemCode(body) ?? (body.code as string | undefined);
  const errors = (body.errors as { path: string; message: string }[] | undefined) ?? [];
  if (code === 'EXPIRED') {
    const until = errors.find((error) => error.path === 'validUntil')?.message;
    const date = until ? new Date(until) : null;
    return {
      kind: 'EXPIRED',
      detail: date
        ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`
        : '',
    };
  }
  if (code === 'RATE_LIMITED') {
    const seconds = Number(errors.find((error) => error.path === 'retryAfter')?.message ?? 600);
    return { kind: 'RATE_LIMITED', detail: String(Math.max(1, Math.ceil(seconds / 60))) };
  }
  if (
    code === 'REVOKED' ||
    code === 'FULL' ||
    code === 'EMPLOYEE_NUMBER_TAKEN' ||
    code === 'AWAITING_APPROVAL'
  ) {
    return { kind: code, detail: '' };
  }
  return { kind: 'UNKNOWN', detail: '' };
}

/** De schil: bovenbalk met bedrijf en taalknop, inhoud, en een vaste voet onderaan. */
function JoinShell({
  company,
  language,
  onLanguage,
  footer,
  children,
}: {
  company: string | null;
  language: JoinLanguage;
  onLanguage: (language: JoinLanguage) => void;
  footer: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Zonder bedrijf het product, niet een vaste naam (MRK-2).
  const productName = useProductName();
  return (
    <div data-zone="app" className="flex min-h-screen flex-col bg-background">
      <header className="flex h-14 items-center justify-between border-b border-border bg-surface px-4">
        <span className="font-heading text-content font-semibold text-text-primary">
          {company ?? productName}
        </span>
        <div className="relative">
          <button
            type="button"
            className="flex h-12 items-center gap-1.5 rounded-pill px-3 font-heading text-small font-semibold text-text-secondary"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            data-testid="join-language"
          >
            <Globe aria-hidden className="h-5 w-5" />
            {language.toUpperCase()}
          </button>
          {open ? (
            <ul
              role="listbox"
              className="absolute right-0 top-12 z-20 w-40 rounded-md border border-border bg-surface p-1 shadow-lg"
            >
              {JOIN_LANGUAGES.map((option) => (
                <li key={option.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.code === language}
                    className="flex min-h-12 w-full items-center justify-between rounded-sm px-3 text-left text-content text-text-primary hover:bg-background"
                    onClick={() => {
                      onLanguage(option.code);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                    {option.code === language ? <Check aria-hidden className="h-4 w-4" /> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col gap-4 px-4 pb-28 pt-5">
        {children}
      </main>
      {footer ? (
        <footer className="fixed inset-x-0 bottom-0 border-t border-border bg-surface px-4 py-3">
          <div className="mx-auto max-w-md">{footer}</div>
        </footer>
      ) : null}
    </div>
  );
}

/** Frame 6 en 5: rond icoonvlak van 72, titel, één of twee zinnen. */
function StateScreen({
  tone,
  icon,
  title,
  text,
  testId,
  children,
}: {
  tone: 'danger' | 'success' | 'neutral';
  icon: ReactNode;
  title: string;
  text: string;
  testId: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 pt-6 text-center" data-testid={testId}>
      <span
        aria-hidden
        className={cn(
          'flex h-[72px] w-[72px] items-center justify-center rounded-full',
          tone === 'danger' && 'bg-danger-tint text-danger-tint-text',
          tone === 'success' && 'bg-success-tint text-success-tint-text',
          tone === 'neutral' && 'bg-border-light text-text-secondary',
        )}
      >
        {icon}
      </span>
      <h1 className="font-heading text-[22px] font-semibold text-text-primary">{title}</h1>
      <p className="max-w-sm text-content text-text-secondary">{text}</p>
      {children}
    </div>
  );
}

/** Twee blokken van vier tekens met een streepje ertussen (frame 1). */
function CodeInput({
  value,
  onChange,
  label,
  hint,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  hint: string;
}) {
  const compact = value.replace('-', '');
  const eerste = useRef<HTMLInputElement>(null);
  const tweede = useRef<HTMLInputElement>(null);
  const blok =
    'h-16 w-full rounded-md border-[1.5px] border-border-strong bg-surface text-center font-heading text-[28px] font-semibold uppercase tracking-[0.18em] text-text-primary focus:border-2 focus:border-[var(--brand-primary-aa)] focus:outline-none focus:ring-4 focus:ring-brand-primary-tint';

  const zet = (links: string, rechts: string) => onChange(formatCode(links + rechts));

  return (
    <fieldset className="flex flex-col gap-2" data-testid="join-code">
      <legend className="mb-2 font-heading text-[15px] font-semibold text-text-primary">
        {label}
      </legend>
      <div className="flex items-center gap-2">
        <input
          ref={eerste}
          aria-label={`${label}, 1–4`}
          className={blok}
          value={compact.slice(0, 4)}
          autoCapitalize="characters"
          autoComplete="one-time-code"
          maxLength={9}
          onChange={(event) => {
            const typed = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (typed.length > 4) {
              // Plakken van de hele code in het eerste vak.
              zet(typed.slice(0, 4), typed.slice(4, 8));
              tweede.current?.focus();
              return;
            }
            zet(typed, compact.slice(4));
            if (typed.length === 4) tweede.current?.focus();
          }}
          data-testid="join-code-1"
        />
        <span aria-hidden className="font-heading text-[28px] text-text-muted">
          –
        </span>
        <input
          ref={tweede}
          aria-label={`${label}, 5–8`}
          className={blok}
          value={compact.slice(4, 8)}
          autoCapitalize="characters"
          maxLength={4}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && compact.length <= 4) eerste.current?.focus();
          }}
          onChange={(event) => {
            const typed = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            zet(compact.slice(0, 4), typed);
          }}
          data-testid="join-code-2"
        />
      </div>
      <p className="text-small text-text-secondary">{hint}</p>
    </fieldset>
  );
}

/**
 * "Scan de QR-code": de camera van de telefoon opent de link zelf, dus de knop legt dat uit in
 * plaats van een eigen scanner te openen (`deviations.md`).
 */
function ScanButton({ label, hint }: { label: string; hint: string }) {
  const [toon, setToon] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="secondary"
        size="lg"
        className="w-full"
        onClick={() => setToon((value) => !value)}
      >
        <QrCode aria-hidden className="mr-2 h-5 w-5" />
        {label}
      </Button>
      {toon ? <p className="text-small text-text-secondary">{hint}</p> : null}
    </div>
  );
}

/** Taalchips helemaal bovenaan (frame 2), in de eigen taal; de gekozen met een vinkje. */
function LanguageChips({
  language,
  onChoose,
}: {
  language: JoinLanguage;
  onChoose: (language: JoinLanguage) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="join-language-chips">
      {JOIN_LANGUAGES.map((option) => {
        const gekozen = option.code === language;
        return (
          <button
            key={option.code}
            type="button"
            aria-pressed={gekozen}
            onClick={() => onChoose(option.code)}
            className={cn(
              'flex min-h-12 items-center gap-1.5 rounded-pill border px-4 font-heading text-small font-semibold',
              gekozen
                ? 'border-[var(--brand-primary-aa)] bg-brand-primary-tint text-[var(--primary-tint-text-aa)]'
                : 'border-border-strong bg-surface text-text-secondary',
            )}
          >
            {gekozen ? <Check aria-hidden className="h-4 w-4" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CompanyTile({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  return logoUrl ? (
    <img src={logoUrl} alt="" className="h-12 w-12 shrink-0 rounded-md object-contain" />
  ) : (
    <span
      aria-hidden
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-brand-dark-blue font-heading text-content font-semibold text-text-on-dark"
    >
      {initials(name)}
    </span>
  );
}

/** "Stap 1 van 3" met een balk in drie delen van 6 hoog. */
function Progress({ step, label }: { step: number; label: string }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-small font-semibold text-text-secondary">{label}</span>
      <div className="flex gap-1.5" aria-hidden>
        {[1, 2, 3].map((index) => (
          <span
            key={index}
            className={cn(
              'h-1.5 flex-1 rounded-pill',
              index <= step ? 'bg-[var(--brand-primary-aa)]' : 'bg-border',
            )}
          />
        ))}
      </div>
    </div>
  );
}

/** Een veld met het juiste invoertype, en "verplicht" of "optioneel" in woorden. */
function FieldInput({
  field,
  value,
  error,
  suffix,
  onChange,
}: {
  field: RegistrationField;
  value: string;
  error: string | undefined;
  suffix: string;
  onChange: (value: string) => void;
}) {
  const label = `${field.label} · ${suffix}`;
  const testId = `join-field-${field.key}`;
  if (field.type === 'select' && field.options) {
    return (
      <Select
        label={label}
        value={value}
        hint={error}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        <option value="">—</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }
  const type = field.type === 'date' ? 'date' : 'text';
  const inputMode =
    field.type === 'number' || field.type === 'employee_number' ? 'numeric' : undefined;
  return (
    <Input
      label={label}
      type={type}
      inputMode={inputMode}
      value={value}
      error={error}
      onChange={(event) => onChange(event.target.value)}
      data-testid={testId}
    />
  );
}

/** `/start/$code`: de link uit de QR-code. */
export function AppJoinStartPage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const normalized = useMemo(() => code ?? null, [code]);
  return <AppJoinPage initialCode={normalized} />;
}
